import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    FaTerminal, FaCodeBranch, FaWindows, FaLinux, FaNodeJs, FaReact, FaDocker,
    FaDatabase, FaPython, FaCloud, FaNetworkWired, FaLock, FaServer, FaGithub,
    FaBoxOpen, FaCode, FaMobileAlt, FaCubes, FaSearch, FaKeyboard, FaRocket, FaCertificate, FaTools, FaChartBar, FaSlidersH,
} from "react-icons/fa";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { EmptyState } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { COMMAND_GROUPS, COMMAND_CATEGORIES, COMMAND_TOTALS } from "../_DATA/commandsCatalog.data";
import { getCategoryTopics, resolveCommandTopic } from "../_DATA/commandTopics.data";
import { CommandCategoryModal } from "../_UI/CommandCategoryModal";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import "../_UI/_Styles/Commands.css";

const ICONS = {
    git: <FaCodeBranch />, linux: <FaLinux />, cmd: <FaWindows />, powershell: <FaWindows />, wsl: <FaLinux />,
    npm: <FaNodeJs />, yarn: <FaNodeJs />, pnpm: <FaBoxOpen />, node: <FaNodeJs />, typescript: <FaCode />,
    react: <FaReact />, "react-native": <FaMobileAlt />, nest: <FaServer />,
    docker: <FaDocker />, compose: <FaDocker />, kubernetes: <FaCubes />, terraform: <FaCloud />, aws: <FaCloud />,
    github: <FaGithub />, ssh: <FaLock />, curl: <FaNetworkWired />, network: <FaNetworkWired />,
    postgres: <FaDatabase />, mysql: <FaDatabase />, redis: <FaDatabase />, python: <FaPython />,
    processes: <FaTerminal />, packages: <FaBoxOpen />,
    "deploy-automation": <FaRocket />, "hostinger-vps": <FaServer />, pm2: <FaNodeJs />, certbot: <FaCertificate />,
    vercel: <FaCloud />, netlify: <FaCloud />, wrangler: <FaCloud />, firebase: <FaCloud />, supabase: <FaDatabase />,
    railway: <FaRocket />, flyctl: <FaRocket />, "github-actions": <FaGithub />, "gitlab-cli": <FaCodeBranch />,
    "jenkins-cli": <FaTools />, proxmox: <FaCubes />,
};

const matchesCategory = (category, search) => {
    if (!search.trim()) return category.commands;
    return category.commands.filter((item) => onMatchesSearch(search, [
        category.title,
        category.description,
        ...(category.aliases || []),
        item.command,
        resolveCommandTopic(category.id, item),
        item.description,
        item.example,
        item.expected,
        item.note,
    ]));
};

const CategoryCard = ({ category, matchedCommands, onOpen, search }) => {
    const basic = category.commands.filter((item) => item.level === "basic").length;
    const intermediate = category.commands.filter((item) => item.level === "intermediate").length;
    const advanced = category.commands.filter((item) => item.level === "advanced").length;
    const preview = (search ? matchedCommands : category.commands).slice(0, 3);
    const topics = getCategoryTopics(category);

    return (
        <button type="button" className="LStudio__commands_category_card" onClick={onOpen}>
            <span className="LStudio__commands_category_icon">{ICONS[category.id] || <FaTerminal />}</span>
            <span className="LStudio__commands_category_content">
                <span className="LStudio__commands_category_head">
                    <strong>{category.title}</strong>
                    <em>{search ? `${matchedCommands.length} coincidencias` : `${category.commands.length} comandos`}</em>
                </span>
                <span className="LStudio__commands_category_description">{category.description}</span>
                <span className="LStudio__commands_category_preview">
                    {preview.map((item) => <code key={item.command}>{item.command}</code>)}
                </span>
                <span className="LStudio__commands_category_levels">
                    <span>{basic} básicos</span>
                    <span>{intermediate} intermedios</span>
                    <span>{advanced} avanzados</span>
                    <span>{topics.length} {topics.length === 1 ? "tema" : "temas"}</span>
                    {category.id === "git" && (
                        <span>{category.commands.filter((item) => resolveCommandTopic(category.id, item) === "Remotos y colaboración").length} remotos</span>
                    )}
                </span>
            </span>
        </button>
    );
};

export const CommandsViewMain = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [modalSearch, setModalSearch] = useState("");
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ group: "ALL", category: "ALL", level: "ALL" });

    const grouped = useMemo(() => COMMAND_GROUPS.map((group) => {
        if (advanced.group !== "ALL" && group.id !== advanced.group) return { ...group, categories: [] };
        const categories = COMMAND_CATEGORIES
            .filter((category) => category.group === group.id)
            .filter((category) => advanced.category === "ALL" || category.id === advanced.category)
            .map((category) => ({
                category,
                matchedCommands: matchesCategory(category, search).filter((item) => advanced.level === "ALL" || item.level === advanced.level),
            }))
            .filter((entry) => entry.matchedCommands.length > 0);
        return { ...group, categories };
    }).filter((group) => group.categories.length > 0), [search, advanced]);

    const matchingCount = useMemo(() => grouped.reduce((total, group) => (
        total + group.categories.reduce((sum, entry) => sum + entry.matchedCommands.length, 0)
    ), 0), [grouped]);
    const advancedCount = [advanced.group !== "ALL", advanced.category !== "ALL", advanced.level !== "ALL"].filter(Boolean).length;
    const clearAdvanced = () => setAdvanced({ group: "ALL", category: "ALL", level: "ALL" });

    useEffect(() => {
        const categoryId = searchParams.get("category");
        if (!categoryId) {
            if (selected) setSelected(null);
            return;
        }
        const category = COMMAND_CATEGORIES.find((item) => String(item.id) === String(categoryId));
        if (category && String(selected?.id || "") !== String(category.id)) {
            setSelected(category);
            setModalSearch(searchParams.get("q") || "");
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const onOpenCategory = (category) => {
        setSelected(category);
        setModalSearch(search);
        const next = new URLSearchParams(searchParams);
        next.set("category", category.id);
        if (search.trim()) next.set("q", search.trim());
        else next.delete("q");
        setSearchParams(next, { replace: false });
    };

    const onCloseCategory = () => {
        setSelected(null);
        const next = new URLSearchParams(searchParams);
        next.delete("category");
        next.delete("q");
        setSearchParams(next, { replace: true });
    };

    return (
        <ViewLayout
            header={<PageHeader title="Comandos" icon={<FaKeyboard />}
                description="Enciclopedia práctica de comandos: terminales, Git, desarrollo, bases de datos, DevOps, deploy, hosting, CI/CD, cloud y virtualización. De lo inicial a lo hardcore."
                actions={<AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />} />}
            toolbar={<ViewToolbar filters={<>
                <SearchFilter value={search} onChange={setSearch}
                    total={COMMAND_TOTALS.total} matches={matchingCount}
                    placeholder="Buscar comando: git remote, pm2, Hostinger, Proxmox, deploy, kubectl..." />
                <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>
                    {advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}
                </AppButton>
            </>} expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                <FieldSelect id="commands_adv_group" label="Área" value={advanced.group}
                    onChange={(event) => setAdvanced((current) => ({ ...current, group: event.target.value }))}
                    options={[{ value: "ALL", label: "Todas" }, ...COMMAND_GROUPS.map((group) => ({ value: group.id, label: group.label }))]} />
                <FieldSelect id="commands_adv_category" label="Tecnología" value={advanced.category}
                    onChange={(event) => setAdvanced((current) => ({ ...current, category: event.target.value }))}
                    options={[{ value: "ALL", label: "Todas" }, ...COMMAND_CATEGORIES.map((category) => ({ value: category.id, label: category.title }))]} />
                <FieldSelect id="commands_adv_level" label="Nivel" value={advanced.level}
                    onChange={(event) => setAdvanced((current) => ({ ...current, level: event.target.value }))}
                    options={[{ value: "ALL", label: "Todos" }, { value: "basic", label: "Básico" }, { value: "intermediate", label: "Intermedio" }, { value: "advanced", label: "Avanzado" }]} />
            </AdvancedFilterPanel>} />}>

            {grouped.length === 0 ? (
                <EmptyState size="lg" icon={<FaSearch />} title="No hay coincidencias"
                    description="Cambiá la búsqueda para volver a ver comandos y tecnologías."
                    action={<AppButton variant="outline" tone="neutral" onClick={() => { setSearch(""); clearAdvanced(); }}>Limpiar filtros</AppButton>} />
            ) : grouped.map((group) => (
                <section key={group.id} className="LStudio__commands_group">
                    <div className="LStudio__commands_group_head">
                        <div>
                            <h2>{group.label}</h2>
                            <p>{group.description}</p>
                        </div>
                        <span>{group.categories.length} {group.categories.length === 1 ? "sección" : "secciones"}</span>
                    </div>
                    <div className="LStudio__commands_grid">
                        {group.categories.map(({ category, matchedCommands }) => (
                            <CategoryCard key={category.id} category={category} matchedCommands={matchedCommands}
                                search={search.trim()} onOpen={() => onOpenCategory(category)} />
                        ))}
                    </div>
                </section>
            ))}

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Comandos" description="Resumen del catálogo de comandos disponible en NOTEBOOK."
                metrics={[
                    { key: "total", label: "Comandos", value: COMMAND_TOTALS.total, icon: <FaTerminal />, tone: "accent" },
                    { key: "tech", label: "Tecnologías", value: COMMAND_CATEGORIES.length, icon: <FaCode />, tone: "neutral" },
                    { key: "basic", label: "Básicos", value: COMMAND_TOTALS.basic, icon: <FaKeyboard />, tone: "neutral" },
                    { key: "intermediate", label: "Intermedios", value: COMMAND_TOTALS.intermediate, icon: <FaTools />, tone: "neutral" },
                    { key: "advanced", label: "Avanzados", value: COMMAND_TOTALS.advanced, icon: <FaRocket />, tone: "ok" },
                    { key: "groups", label: "Áreas", value: COMMAND_GROUPS.length, icon: <FaCubes />, tone: "neutral" },
                ]} />
            <CommandCategoryModal key={`${selected?.id || "none"}-${modalSearch}`} category={selected}
                isOpen={!!selected} onClose={onCloseCategory} initialSearch={modalSearch} />
        </ViewLayout>
    );
};
